import { useStripe, useElements } from '@stripe/react-stripe-js';
import { FormikHelpers } from 'formik';
import { DependencyList, useCallback } from 'react';
import { TApi } from 'libs/api';

export interface IStripeCardFormValues {
  credit_card_number?: string;
  credit_card_expiry?: string;
  credit_card_cvc?: string;
  stripe_payment_method_id?: string;
}

const CVC_ERROR_CODES = ['incorrect_cvc', 'invalid_cvc'];
const EXPIRY_ERROR_CODES = ['invalid_expiry_month', 'invalid_expiry_year', 'expired_card'];

function getStripeErrorField(code?: string): string {
  if (code && CVC_ERROR_CODES.includes(code)) return 'credit_card_cvc';
  if (code && EXPIRY_ERROR_CODES.includes(code)) return 'credit_card_expiry';
  return 'credit_card_number';
}

function getSetupIntentStatusMessage(status?: string): string {
  if (status === 'requires_action')
    return 'Additional authentication is required. Please try again.';
  if (status === 'requires_payment_method')
    return 'Card was declined. Please try a different card.';
  return 'Card setup failed. Please try again.';
}

interface IUseStripeCardSetup<T extends IStripeCardFormValues> {
  setupIntentApi: TApi;
  params: object;
  onSubmit: (values: T, form: FormikHelpers<T>) => void;
  deps?: DependencyList;
}

export default function useStripeCardSetup<T extends IStripeCardFormValues>({
  setupIntentApi,
  params,
  onSubmit,
  deps = [],
}: IUseStripeCardSetup<T>) {
  const stripe = useStripe();
  const elements = useElements();

  return useCallback(
    async (values: T, form: FormikHelpers<T>) => {
      form.setSubmitting(true);
      if (!stripe || !elements) {
        form.setSubmitting(false);
        return;
      }

      const cardNumber = elements.getElement('cardNumber');
      if (!cardNumber) {
        form.setSubmitting(false);
        return;
      }

      let resource;
      try {
        const { data } = await setupIntentApi(params);
        resource = data.resource;
      } catch {
        form.setFieldTouched('credit_card_number', true, false);
        form.setFieldError(
          'credit_card_number',
          'Failed to initialize card setup. Please try again.'
        );
        form.setSubmitting(false);
        return;
      }

      const { setupIntent, error } = await stripe.confirmCardSetup(resource.client_secret, {
        payment_method: { card: cardNumber },
      });

      if (error) {
        const errorField = getStripeErrorField(error.code);
        form.setFieldTouched(errorField, true, false);
        form.setFieldError(errorField, error.message || 'Card setup failed');
        form.setSubmitting(false);
        return;
      }

      if (setupIntent?.status !== 'succeeded') {
        form.setFieldTouched('credit_card_number', true, false);
        form.setFieldError('credit_card_number', getSetupIntentStatusMessage(setupIntent?.status));
        form.setSubmitting(false);
        return;
      }

      const { credit_card_number, credit_card_expiry, credit_card_cvc, ...formValues } = values;
      onSubmit(
        { stripe_payment_method_id: setupIntent.payment_method as string, ...formValues } as T,
        form
      );
    },
    [stripe, elements, setupIntentApi, onSubmit, ...deps]
  );
}
